import { createServerFn } from "@tanstack/react-start";

export type ActivityKind = "photobooth" | "market" | "truck" | "dj" | "other";

export type PublicActivity = {
  id: string;
  film_id: string;
  kind: ActivityKind;
  title: string;
  host: string | null;
  summary: string | null;
  body: string | null;
  start_time: string | null;
  end_time: string | null;
  image_url: string | null;
  link_url: string | null;
  sort_order: number;
};

function timeKey(t: string | null): number {
  if (!t) return 9999;
  const m = /^(\d{1,2})[:.]?(\d{2})?/.exec(t.trim());
  if (!m) return 9999;
  return Number(m[1]) * 60 + Number(m[2] ?? 0);
}

/**
 * Public pre-show programme, grouped per film id. Only visible items.
 */
export const getFilmActivities = createServerFn({ method: "GET" }).handler(
  async (): Promise<Record<string, PublicActivity[]>> => {
    const { cached } = await import("@/lib/server-cache.server");
    return cached("film-activities", 120_000, async () => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data, error } = await (supabaseAdmin as any)
      .from("film_activities")
      .select(
        "id,film_id,kind,title,host,summary,body,start_time,end_time,image_url,link_url,sort_order,visible",
      )
      .eq("visible", true)
      .order("sort_order", { ascending: true });

    if (error || !data) return {};

    const out: Record<string, PublicActivity[]> = {};
    for (const row of data as Array<Record<string, unknown>>) {
      const raw = (row.image_url as string | null) ?? null;
      let image: string | null = null;
      if (raw) {
        if (/^https?:\/\//i.test(raw)) {
          image = raw;
        } else {
          const { data: signed } = await supabaseAdmin.storage
            .from("film-activities")
            .createSignedUrl(raw, 60 * 60 * 24 * 7);
          image = signed?.signedUrl ?? null;
        }
      }
      const item: PublicActivity = {
        id: row.id as string,
        film_id: row.film_id as string,
        kind: ((row.kind as string) || "other") as ActivityKind,
        title: row.title as string,
        host: (row.host as string | null) ?? null,
        summary: (row.summary as string | null) ?? null,
        body: (row.body as string | null) ?? null,
        start_time: (row.start_time as string | null) ?? null,
        end_time: (row.end_time as string | null) ?? null,
        image_url: image,
        link_url: (row.link_url as string | null) ?? null,
        sort_order: (row.sort_order as number) ?? 0,
      };
      (out[item.film_id] ??= []).push(item);
    }

    for (const key of Object.keys(out)) {
      out[key].sort(
        (a, b) =>
          a.sort_order - b.sort_order ||
          timeKey(a.start_time) - timeKey(b.start_time) ||
          a.title.localeCompare(b.title),
      );
    }
    return out;
    });
  },
);
